import { useEffect, useLayoutEffect, useRef, useState, type ChangeEvent } from 'react';
import { prepararArraste, useAlvoDeArraste, useAlvoNoDocumento } from '../input/arraste';
import { api } from '../api';
import { ApiError, type Banco, type BancoItem, type Pack, type PackItem } from '../api/types';
import { numero, t } from '../i18n/idioma';
import { nomeDoItem } from '../i18n/nomes';
import { iconeDaPrata, iconeDoOuro } from '../assets/items';
import { ItemIcon } from './ItemIcon';
import { formatGold, stones } from './itemUi';
import { DRAG_ITEM } from './LootGump';
import { useQuantas } from './Quantas';

interface Props {
  npcId: string;
  active: boolean;
  tick: number;
  onPack(pack: Pack): void;
  onErro(text: string): void;
}

/** Item do cofre na mão (arraste). Outra chave que a da mochila: os alvos dela não o pegam. */
export const DRAG_BANCO = 'text/bank-item';

type Moeda = 'silver' | 'gold';
const MOEDAS: readonly Moeda[] = ['silver', 'gold'];
const ATALHOS = [1_000, 10_000, 100_000] as const;
const QTD_MAX = 2_147_483_647;

/**
 * A quantidade só aceita números (Renato, 03/11): o estado guarda os dígitos, e o campo mostra com separador de milhar
 * (6.004.747). Letras, ponto, vírgula e espaço são descartados ao digitar ou colar.
 */
function soDigitos(bruto: string): string {
  const d = bruto.replace(/\D/g, '').replace(/^0+/, '');
  return d.length > 10 ? d.slice(0, 10) : d;
}
function lerQtd(digitos: string): number {
  const n = Number(digitos);
  return Number.isFinite(n) && n > 0 ? Math.min(n, QTD_MAX) : 0;
}

const BANK_COLS = 10;
const BANK_ROWS = 5;
const ICON = 28;

export function BankGump({ npcId, active, tick, onPack, onErro }: Props) {
  const [banco, setBanco] = useState<Banco | null>(null);
  // Moedas (protótipo banco-moedas, opção A, Renato 03/11/2026): uma aba por moeda e uma linha de ações só.
  const [moeda, setMoeda] = useState<Moeda>('silver');
  const [qtd, setQtd] = useState('1000');
  // Onde fica o cursor depois de formatar: quantos dígitos ficam à direita dele (os pontos mudam de lugar ao digitar).
  const qtdRef = useRef<HTMLInputElement>(null);
  const digitosADireita = useRef<number | null>(null);
  useLayoutEffect(() => {
    const el = qtdRef.current;
    const direita = digitosADireita.current;
    digitosADireita.current = null;
    if (!el || direita === null || document.activeElement !== el) return;
    let pos = el.value.length;
    for (let vistos = 0; pos > 0 && vistos < direita; pos--) if (/\d/.test(el.value[pos - 1])) vistos++;
    el.setSelectionRange(pos, pos);
  }, [qtd]);
  const mudarQtd = (e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    const fim = e.target.selectionEnd ?? v.length;
    digitosADireita.current = v.slice(fim).replace(/\D/g, '').length;
    setQtd(soDigitos(v));
  };
  const [mochila, setMochila] = useState<{ silver: number; gold: number } | null>(null);
  /** Os itens da mochila: o arraste para o cofre só traz o id, e o "Quantas?" precisa da pilha. */
  const [soltos, setSoltos] = useState<readonly PackItem[]>([]);
  const [busy, setBusy] = useState(false);
  const quantas = useQuantas();

  useEffect(() => {
    if (!active) return;
    let live = true;
    api.getBanco(npcId)
      .then(next => { if (live) setBanco(next); })
      .catch(err => { if (live) onErro(err instanceof ApiError ? err.message : t('comum.recusouPedido')); });
    api.getPack().then(p => {
      if (!live) return;
      setMochila({ silver: p.silver ?? 0, gold: p.gold ?? 0 });
      setSoltos(p.items.filter(i => !i.equippedSlot));
    }).catch(() => {});
    return () => { live = false; };
    // onErro é callback do pai — não reabre o cofre a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, npcId, tick]);

  const aplicar = (move: { banco: Banco; pack: Pack }) => {
    setBanco(move.banco);
    setMochila({ silver: move.pack.silver ?? 0, gold: move.pack.gold ?? 0 });
    setSoltos(move.pack.items.filter(i => !i.equippedSlot));
    onPack(move.pack);
  };

  const agir = async (fn: () => Promise<{ banco: Banco; pack: Pack }>) => {
    if (busy) return;
    setBusy(true);
    try {
      aplicar(await fn());
    } catch (err) {
      onErro(err instanceof ApiError ? err.message : t('comum.recusouPedido'));
    } finally {
      setBusy(false);
    }
  };

  const noCofre = (m: Moeda) => (m === 'silver' ? banco?.prata : banco?.ouro) ?? 0;
  const naMochila = (m: Moeda) => mochila?.[m] ?? 0;
  const n = lerQtd(qtd);
  const nomeMoeda = t(moeda === 'silver' ? 'banco.prata' : 'banco.ouro').toLowerCase();
  const sacar = () => { if (n > 0) void agir(() => api.sacarMoeda(npcId, { moeda, qtd: n })); };
  // O valor igual ao que tem na mochila vai como "tudo": a prata que chegou no meio do caminho entra junto.
  const depositar = () => {
    if (n <= 0) return;
    void agir(() => api.depositarMoeda(npcId, n >= naMochila(moeda) ? { moeda, tudo: true } : { moeda, qtd: n }));
  };
  const max = banco?.vagasMax ?? BANK_COLS * BANK_ROWS;
  const itens = banco?.itens ?? [];
  const slots = Math.max(max, Math.ceil(Math.max(itens.length, 1) / BANK_COLS) * BANK_COLS);
  const cells: Array<BancoItem | null> = Array.from(
    { length: slots },
    (_, i) => itens[i] ?? null
  );
  const peso = banco?.peso ?? 0;
  const pesoMax = banco?.pesoMax ?? 800;

  // Pilha pergunta "Quantas?" nos dois sentidos (techspec-pilha-de-pecas §13).
  const guardarItem = async (id: string) => {
    if (busy) return;
    const item = soltos.find(i => i.id === id);
    const n = item ? await quantas.pedir({ defId: item.defId, nome: item.name, max: item.qty, titulo: t('quantas.guardarBanco') }) : undefined;
    if (n === null) return;
    void agir(() => api.guardarBanco(npcId, { itemId: id, qty: n }));
  };
  const sacarItem = async (id: string) => {
    if (busy) return;
    const item = itens.find(i => i.id === id);
    const n = item ? await quantas.pedir({ defId: item.defId, nome: item.nome, max: item.qty, titulo: t('quantas.sacarBanco') }) : undefined;
    if (n === null) return;
    void agir(() => api.sacarBanco(npcId, { bankItemId: id, qty: n }));
  };

  // Item da mochila solto no cofre: guarda.
  const gradeRef = useRef<HTMLDivElement>(null);
  useAlvoDeArraste(gradeRef, {
    sobre: ({ carga }) => !busy && !!carga[DRAG_ITEM],
    solta: ({ carga }) => {
      const id = carga[DRAG_ITEM];
      if (id) void guardarItem(id);
    }
  });

  // Item do cofre solto fora da grade do cofre (na mochila, no boneco, no mundo): saca para a mochila
  // (Renato, 16/10: antes só dava com clique).
  useAlvoNoDocumento({
    sobre: ({ carga, alvo }) => !busy && !!carga[DRAG_BANCO] && !gradeRef.current?.contains(alvo),
    solta: ({ carga }) => {
      const id = carga[DRAG_BANCO];
      if (id) void sacarItem(id);
    }
  }, active);

  return (
    <div className="banco-gump">
      <p className="pack-pane__label banco-gump__ocupacao">
        <span>{t('banco.cofreDe', { usados: banco?.vagas ?? itens.length, max })}</span>
        <span>{stones(peso)} / {stones(pesoMax)} {t('comum.stones')}</span>
      </p>
      <div
        ref={gradeRef}
        className="pack-grid banco-grid"
        aria-label={itens.length === 0 ? t('banco.vazio') : t('banco.cofreDe', { usados: banco?.vagas ?? itens.length, max })}
      >
        {cells.map((item, i) => (
          <div
            key={item ? item.id : `empty-${i}`}
            className={`pack-cell${item ? '' : ' is-empty'}`}
            title={item ? nomeDoItem(item.defId, item.nome) : undefined}
          >
            {item && (
              <button
                type="button"
                className="pack-cell__hit"
                disabled={busy}
                onPointerDown={e => { if (!busy) prepararArraste(e, { [DRAG_BANCO]: item.id }); }}
                onClick={() => { void sacarItem(item.id); }}
              >
                <ItemIcon
                  id={item.defId}
                  label={nomeDoItem(item.defId, item.nome)}
                  size={ICON}
                  quantity={item.qty}
                  eager
                />
              </button>
            )}
          </div>
        ))}
      </div>
      <div className="banco-moedas">
        <div className="banco-moedas__abas" role="tablist" aria-label={t('banco.moedas')}>
          {MOEDAS.map(m => (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={moeda === m}
              className={`banco-moedas__aba${moeda === m ? ' is-on' : ''}`}
              onClick={() => setMoeda(m)}
            >
              <small>{t('banco.noCofre', { moeda: t(m === 'silver' ? 'banco.prata' : 'banco.ouro') })}</small>
              <span className={`banco-moedas__saldo${m === 'gold' ? ' pack-gold' : ''}`} title={numero(noCofre(m))}>
                <img src={m === 'silver' ? iconeDaPrata() : iconeDoOuro()} alt="" width={16} height={16} draggable={false} />
                {formatGold(noCofre(m))}
              </span>
              <small title={numero(naMochila(m))}>{t('banco.naMochila', { n: formatGold(naMochila(m)) })}</small>
            </button>
          ))}
        </div>
        <div className="banco-moedas__chips">
          {ATALHOS.map(v => (
            <button key={v} type="button" className="banco-moedas__chip" disabled={busy} onClick={() => setQtd(soDigitos(String(v)))}>{formatGold(v)}</button>
          ))}
          <button type="button" className="banco-moedas__chip" disabled={busy} onClick={() => setQtd(soDigitos(String(noCofre(moeda))))}>{t('banco.tudoCofre')}</button>
          <button type="button" className="banco-moedas__chip" disabled={busy} onClick={() => setQtd(soDigitos(String(naMochila(moeda))))}>{t('banco.tudoMochila')}</button>
        </div>
        <div className="banco-moedas__linha">
          <input
            className="banco-moedas__qtd"
            type="text"
            inputMode="numeric"
            ref={qtdRef}
            value={qtd ? numero(Number(qtd)) : ''}
            onChange={mudarQtd}
            onKeyDown={e => { if (e.key === 'Enter') sacar(); }}
            aria-label={t('banco.qtd')}
            disabled={busy}
          />
          <button type="button" className="btn btn--tiny" disabled={busy || n <= 0} onClick={sacar}>{t('banco.sacar')}</button>
          <button type="button" className="btn btn--tiny btn--primary" disabled={busy || n <= 0} onClick={depositar}>{t('banco.depositar')}</button>
        </div>
        <p className="banco-moedas__dica" aria-live="polite">
          {n > 0 ? t('banco.qtdDe', { n: numero(n), moeda: nomeMoeda }) : t('banco.qtdVazia')}
        </p>
      </div>
      {quantas.dialogo}
    </div>
  );
}
